import { Link } from "react-router-dom";
import { ArrowUpRight, Info } from "lucide-react";
import { Reveal, Eyebrow, RevealLines } from "@/components/Reveal";
import { GroupMark } from "@/components/GroupMark";
import Seo from "@/components/Seo";

const COMPANIES = [
  {
    key: "estate",
    name: "IDEAL REAL ESTATE",
    tag: "Compravendita e locazioni immobiliari.",
    desc: "È la società dedicata alla consulenza immobiliare, alla compravendita e alla locazione di immobili a Milano e in Lombardia.",
    to: "/",
    cta: "Vai al sito",
  },
  {
    key: "rent",
    name: "IDEAL RENT",
    tag: "Short rent & property management.",
    desc: "È la società del gruppo specializzata negli affitti brevi e nella gestione degli immobili destinati alle locazioni turistiche.",
    to: "/contatti?motivo=Consulenza",
    cta: "Contatta la società",
  },
  {
    key: "ready",
    name: "IDEAL READY",
    tag: "Manutenzione e pronto intervento.",
    desc: "È la società del gruppo dedicata agli interventi tecnici, alla manutenzione e all'assistenza operativa per gli immobili.",
    to: "/contatti?motivo=Consulenza",
    cta: "Contatta la società",
  },
  {
    key: "construction",
    name: "IDEAL RISTRUTTURA",
    tag: "Ristrutturazione e costruzione.",
    desc: "È la società del gruppo dedicata alla ristrutturazione degli immobili, dal progetto alla consegna dei cantieri.",
    to: "/contatti?motivo=Consulenza",
    cta: "Contatta la società",
  },
];

export default function IlGruppo() {
  return (
    <div data-testid="gruppo-page" className="pt-28 lg:pt-32">
      <Seo
        title="IDEAL GROUP — Le quattro società del gruppo | IDEAL"
        description="IDEAL GROUP integra compravendite, affitti brevi, manutenzione e ristrutturazione: IDEAL REAL ESTATE, IDEAL RENT, IDEAL READY e IDEAL RISTRUTTURA."
        path="/il-gruppo"
      />
      <header className="mx-auto max-w-[1440px] px-6 pb-20 lg:px-12 lg:pb-28">
        <Reveal><Eyebrow>IDEAL GROUP</Eyebrow></Reveal>
        <h1>
          <RevealLines
            lines={["Un unico gruppo,", "competenze complementari."]}
            className="mt-5 max-w-5xl font-serif text-4xl font-medium leading-[1.05] lg:text-6xl"
          />
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-ink2 lg:text-lg">
            IDEAL REAL ESTATE fa parte di IDEAL GROUP, un gruppo che integra competenze
            immobiliari, gestione degli affitti brevi e assistenza tecnica, con l'obiettivo
            di offrire servizi complementari dedicati al mondo immobiliare.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-12">
        <div className="grid gap-8 lg:grid-cols-2">
          {COMPANIES.map((c, i) => (
            <Reveal key={c.key} delay={(i % 2) * 0.1} className="h-full">
              <article
                data-testid={`group-card-${c.key}`}
                className="flex h-full flex-col border border-ink/10 bg-sand2 p-10 transition-colors duration-500 hover:bg-sand lg:p-12"
              >
                <GroupMark company={c.key} className="h-14 w-auto" />
                <h2 className="mt-12 font-serif text-[26px] font-semibold tracking-[0.08em]">{c.name}</h2>
                <p className="mt-2 font-serif text-lg italic text-champagne">{c.tag}</p>
                <p className="mt-6 text-[15px] font-light leading-relaxed text-mut">{c.desc}</p>
                <Link
                  to={c.to}
                  className="group mt-auto inline-flex items-center gap-2 pt-12 text-[12px] font-medium uppercase tracking-wide2 text-ink transition-colors hover:text-champagne"
                >
                  <span className="border-b border-ink/30 pb-1 transition-colors group-hover:border-champagne">{c.cta}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 flex items-start gap-4 border border-ink/10 bg-sand p-6 lg:items-center lg:gap-5 lg:px-10">
            <Info className="h-5 w-5 shrink-0 text-champagne" />
            <p className="text-[14px] font-light leading-relaxed text-ink2">
              IDEAL RENT, IDEAL READY e IDEAL RISTRUTTURA sono società appartenenti al gruppo
              IDEAL e operano autonomamente: non sono servizi offerti direttamente da IDEAL REAL
              ESTATE, ma realtà complementari dello stesso gruppo.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-ink text-sand">
        <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
          <RevealLines
            lines={["Quattro società, un unico", "standard di qualità."]}
            className="font-serif text-4xl font-medium leading-[1.05] lg:text-5xl"
          />
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-sand/60">
              Dalla consulenza per la compravendita alla gestione degli affitti brevi, dalla
              manutenzione alla costruzione e ristrutturazione: il gruppo IDEAL copre l'intero
              ciclo di vita dell'immobile.
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-12">
            <Link
              to="/contatti"
              className="group inline-flex items-center gap-3 rounded-full bg-sand px-9 py-4 text-[12px] font-medium uppercase tracking-wide2 text-ink transition-colors duration-300 hover:bg-champagne hover:text-sand"
            >
              Parla con noi
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
